import { Suspense, useEffect, useState, type ReactNode } from "react";

/**
 * Renders children only after the browser is idle (or after `delay` ms),
 * so non-critical UI never competes with first paint / hydration.
 */
export function DeferUntilIdle({
  children,
  delay = 1500,
}: {
  children: ReactNode;
  delay?: number;
}) {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const go = () => setReady(true);
    const ric = (
      window as unknown as {
        requestIdleCallback?: (cb: () => void, o?: { timeout: number }) => number;
      }
    ).requestIdleCallback;
    if (typeof ric === "function") {
      const id = ric(go, { timeout: delay });
      return () => {
        const cancel = (window as unknown as { cancelIdleCallback?: (id: number) => void })
          .cancelIdleCallback;
        cancel?.(id);
      };
    }
    const id = window.setTimeout(go, delay);
    return () => window.clearTimeout(id);
  }, [delay]);

  if (!ready) return null;
  return <Suspense fallback={null}>{children}</Suspense>;
}
